import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { IdField } from '~/components/Fields';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { AvailableStructureSet } from '~/hooks/structure-set/useAvailableStructureSets';
import { dicomViewerAtom } from '~/state/atoms/viewerAtoms';
import PropertiesPanel from './PropertiesPanel';

interface StructureSetPropertiesProps {
    structureSet: AvailableStructureSet;
    onDelete?: () => void;
}

export default function StructureSetProperties({
    structureSet,
    onDelete,
}: StructureSetPropertiesProps) {
    const intl = useIntl();
    const dash = '\u2014';
    const obj = structureSet.structureSetObject;
    const setDicomViewer = useSetRecoilState(dicomViewerAtom);

    const datasetWrapper = obj?.dicomStructureSet.datasetWrapper;
    const onViewDicom = useCallback(() => {
        if (!datasetWrapper) {
            return;
        }
        setDicomViewer({
            dataset: datasetWrapper,
            title: structureSet.label ?? 'Structure Set',
        });
    }, [datasetWrapper, structureSet.label, setDicomViewer]);

    return (
        <PropertiesPanel
            onViewDicom={datasetWrapper ? onViewDicom : undefined}
            onDelete={onDelete}
        >
            <LabelText>
                <FormattedMessage id="label" />
            </LabelText>
            <ValueText component="span">{structureSet.label || dash}</ValueText>

            {obj && (
                <>
                    <LabelText>
                        <FormattedMessage id="description" />
                    </LabelText>
                    <ValueText component="span">
                        {obj.structureSetDescription || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="approvalStatus" />
                    </LabelText>
                    <ValueText component="span">{obj.approvalStatus}</ValueText>

                    <LabelText>ROIs</LabelText>
                    <ValueText component="span">
                        {obj.numberOfStructures}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="manufacturer" />
                    </LabelText>
                    <ValueText component="span">
                        {obj.manufacturer || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="manufacturerModelName" />
                    </LabelText>
                    <ValueText component="span">
                        {obj.manufacturerModelName || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="softwareVersions" />
                    </LabelText>
                    <ValueText component="span">
                        {obj.softwareVersions || dash}
                    </ValueText>

                    <LabelText>
                        {intl.formatMessage({ id: 'frameOfReferenceUid' })}
                    </LabelText>
                    <IdField
                        id={obj.frameOfReferenceUid}
                        typographyProps={{ fontSize: 11 }}
                    />
                </>
            )}

            {structureSet.imageLabel && (
                <>
                    <LabelText>
                        <FormattedMessage id="registration.sourceImage" />
                    </LabelText>
                    <ValueText component="span">
                        <SensitiveInfoTypography>
                            {structureSet.imageLabel}
                        </SensitiveInfoTypography>
                    </ValueText>
                </>
            )}

            <LabelText>
                {intl.formatMessage({ id: 'sopInstanceUid' })}
            </LabelText>
            <IdField
                id={structureSet.sopInstanceUID}
                typographyProps={{ fontSize: 11 }}
            />
        </PropertiesPanel>
    );
}
